import { useState, useCallback } from 'react';
/**
 * `useItemEditing` is a custom hook designed to manage the editing state of an item from list within a form.
 * It uses local state management, making it suitable for scenarios where you need to handle editing and input changes of an item inside a list.
 * This hook simplifies the process of handling item editing, value changes, and resets within a form.
 *
 * @template T - The type of data for the form item.
 * @param {Partial<T>} defaultValues - The default values for the item when not in edit mode.
 *
 * @returns {UseItemEditingReturn<T>} - An object containing functions and properties for managing item editing:
 * - {Partial<T>} item - The current item being edited.
 * - {(e: React.ChangeEvent<HTMLInputElement>) => void} onChange - Function to handle input changes for the item.
 * - {(name: string, value: string | number | BasicOptionType[]) => void} onSetValue - Function to set the value of a specific field in the item.
 * - {() => void} onReset - Function to reset the item to its default values.
 */
export const useItemEditing = (defaultValues) => {
    const [item, setItem] = useState(defaultValues);
    const onChange = useCallback((e) => {
        const { name, value } = e.target;
        setItem((prevState) => (Object.assign(Object.assign({}, prevState), { [name]: value })));
    }, []);
    const onSetValue = useCallback((name, value) => {
        setItem((prevState) => (Object.assign(Object.assign({}, prevState), { [name]: value })));
    }, []);
    const onReset = useCallback(() => {
        setItem(defaultValues || {});
    }, [defaultValues]);
    return { item, onChange, onSetValue, onReset };
};
